import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Business,
  Language,
  SmartToy,
  ArrowBack,
  ArrowForward,
  CheckCircle
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    website: '',
    agreeToTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = ['Personal Information', 'Account Security', 'Company Details'];

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  // Modified to prevent form submission on step navigation
  const handleNext = (e) => {
    // Prevent form submission when clicking Next
    e.preventDefault();
    
    if (activeStep === 0) {
      // Validate personal info step
      if (!formData.name || !formData.email) {
        setError('Please fill in all required fields.');
        return;
      }
      // Simple email validation
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        setError('Please enter a valid email address.');
        return;
      }
    } else if (activeStep === 1) {
      // Validate security step
      if (!formData.password) {
        setError('Password is required.');
        return;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setError('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      setError('You must agree to the terms and conditions.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const { confirmPassword, agreeToTerms, ...dataToSubmit } = formData;
      
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/register`,
        dataToSubmit
      );
      
      navigate('/login'); // Redirect to login page after successful registration
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'Registration failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render different forms based on the active step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </>
        );
      case 1:
        return (
          <>
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box 
              sx={{ 
                mt: 2, 
                p: 2, 
                borderRadius: 1,
                backgroundColor: 'rgba(76, 133, 240, 0.05)',
                border: '1px solid rgba(76, 133, 240, 0.2)'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Password requirements:
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>At least 8 characters long</li>
                <li>Include at least one uppercase letter</li>
                <li>Include at least one number</li>
                <li>Include at least one special character</li>
              </ul>
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <TextField
              fullWidth
              label="Company Name (Optional)"
              name="company"
              value={formData.company}
              onChange={handleChange}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Website (Optional)"
              name="website"
              value={formData.website}
              onChange={handleChange}
              margin="normal"
              placeholder="https://example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Language color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link to="/terms" style={{ color: theme.palette.primary.main }}>
                    Terms & Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" style={{ color: theme.palette.primary.main }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #2c3e50 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1
        }}
      >
        {[...Array(6)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px'][i % 3],
              height: ['100px', '150px', '200px'][i % 3],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.02})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`
            }}
          />
        ))}
      </Box>

      <Container maxWidth="lg" sx={{ zIndex: 2 }}>
        <Grid container spacing={0} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }}>
          {/* Left side - Hero content (hidden on mobile) */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              pr: 5,
            }}
          >
            <Box 
              component="img"
              src="/robot1.svg" 
              alt="AI Robot"
              sx={{
                width: '70%',
                maxWidth: 400,
                mb: 3,
                filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.2))',
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEyOCIgY3k9IjEyOCIgcj0iNzAiIGZpbGw9IiM0Qzg1RjAiLz48cmVjdCB4PSI4OCIgeT0iODgiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcng9IjE2IiBmaWxsPSIjMjEyMTMxIi8+PHJlY3QgeD0iMTA0IiB5PSIxMDQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgcng9IjgiIGZpbGw9IiM0Qzg1RjAiLz48L3N2Zz4=';
              }}
            />
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                color: 'white',
                fontWeight: 700,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                mb: 2
              }}
            >
              Join GorillaForge AI
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'white',
                opacity: 0.85,
                textAlign: 'center',
                maxWidth: '80%',
                textShadow: '0 1px 5px rgba(0,0,0,0.2)'
              }}
            >
              Build intelligent bots. Unleash AI strength.
              The primal force behind smart automation.
            </Typography>
          </Grid>

          {/* Right side - Registration form */}
          <Grid item xs={12} md={7}>
            <Paper 
              elevation={10}
              sx={{
                p: 4,
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box display="flex" alignItems="center" mb={3}>
                <SmartToy 
                  sx={{ 
                    color: theme.palette.primary.main, 
                    fontSize: '2rem',
                    mr: 1 
                  }} 
                />
                <Typography variant="h4" component="h1" fontWeight="700">
                  Create Account
                </Typography>
              </Box>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={activeStep === steps.length - 1 ? handleSubmit : handleNext}>
                {renderStepContent()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 2 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    sx={{ display: activeStep === 0 ? 'none' : 'flex' }}
                    type="button" // Important: prevent form submission on back
                  >
                    Back
                  </Button>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    type={activeStep === steps.length - 1 ? "submit" : "button"} // Only submit on last step
                    onClick={activeStep === steps.length - 1 ? null : handleNext} // Use handleNext for non-final steps
                    disabled={isSubmitting}
                    endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
                    sx={{
                      py: 1.2,
                      px: 3,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        transform: 'translateX(-100%)',
                        animation: 'shimmer 1.5s infinite'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      activeStep === steps.length - 1 ? 'Complete Registration' : 'Next'
                    )}
                  </Button>
                </Box>
              </form>

              {activeStep === 0 && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>

                  <Box mt={2} textAlign="center">
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      Already have an account?
                    </Typography>
                    <Button
                      component={Link}
                      to="/login"
                      variant="outlined"
                      fullWidth
                    >
                      Sign In
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }
        
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </Box>
  );
};

export default Register;
